/**
 * MessagingTab.jsx - Main Messaging Panel Orchestrator
 * 
 * Translated from MessagingPanel_source.tsx (v1.1-admin-uplift)
 * 
 * Part 1 Checklist Items Covered:
 * - A1: Tab Rail Container
 * - A2: Email Tab Chip
 * - A3: SMS Tab Chip
 * - A4: Activity Log Tab Chip
 * - A5: Guest Journey Tab Chip
 * - A6: Tab Active State Styling
 * - B1: Communication Studio Header
 * - B2: AI Studio Button
 * - B3: Import Template Button
 * - B4: Template Library Filter
 * - B5: Search Input
 * - C1-C9: Pre-Flight HUD (via PreFlightHUD component)
 * - D1-D14: Template Carousel (via TemplateCarousel component)
 * 
 * Part 2 Checklist Items Covered:
 * - E1-E10: Send Composition Zone (via SendComposer component)
 * - F1-F8: Attention Queue & Activity Feed (via ActivityFeed component)
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Mail, MessageSquare, Activity, GitBranch, Sparkles, Upload, Search,
  Send, FileText, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { styles, COLORS } from './messaging/messagingStyles';
import KPIIntegrityIndicator from './KPIIntegrityIndicator';
import PreFlightHUD from './messaging/PreFlightHUD';
import TemplateCarousel from './messaging/TemplateCarousel';
import SendComposer from './messaging/SendComposer';
import ActivityFeed from './messaging/ActivityFeed';
import SMSStudio from './messaging/SMSStudio';
import ActivityLogTab from './messaging/ActivityLogTab';
import JourneyBuilder from './messaging/JourneyBuilder';
import TemplatePreviewModal from './messaging/TemplatePreviewModal';
import TemplateImportModal from './messaging/TemplateImportModal';
import AIGeneratorModal from './messaging/AIGeneratorModal';
import MessageTemplatesTab from './MessageTemplatesTab';
import ComposeTab from './messaging/ComposeTab';
import AdminPageHeader from './AdminPageHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ─── Tab Definitions ────────────────────────────────────────────────────────────

const TAB_DEFS = [
  { key: 'email',    label: 'Email',         icon: Mail },
  { key: 'sms',      label: 'SMS',           icon: MessageSquare },
  { key: 'compose',  label: 'Compose Invite', icon: Send },
  { key: 'activity', label: 'Activity Log',  icon: Activity },
  { key: 'journey',  label: 'Guest Journey', icon: GitBranch },
  { key: 'templates',label: 'Templates',     icon: FileText },
];

// ─── Filter Options ─────────────────────────────────────────────────────────────

const TEMPLATE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'ready', label: 'Ready' },
  { key: 'draft', label: 'Draft' },
];

// ─── Main Component ─────────────────────────────────────────────────────────────

const MessagingTab = ({ guests = [], selectedIds = [], refresh, initialTab = null, integrityCheck = null }) => {
  // ═══ STATE ════════════════════════════════════════════════════════════════════
  
  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab || 'email');
  
  // Update activeTab if initialTab changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  
  // Template state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [templateSearch, setTemplateSearch] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  // Send state
  const [selectedGuests, setSelectedGuests] = useState(selectedIds || []);
  const [isSending, setIsSending] = useState(false);
  
  // Email logs state
  const [emailLogs, setEmailLogs] = useState([]);
  
  // Modal state
  const [showAIModal, setShowAIModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);

  // ─── Item 4 — Milo template-assist prefill consumer ─────────────────────
  // One-shot read from sessionStorage on mount (and again on initialTab
  // change, in case the prefill was set immediately before navigation).
  // Validates the payload shape strictly, then preselects the template
  // and applies the search filter. Recipient checkboxes, subject, body,
  // schedule date, and the Send button are NEVER touched. Banner remains
  // visible until the operator dismisses it. Same pattern as ComposeTab.
  const [miloPrefillBanner, setMiloPrefillBanner] = useState(null);
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem('milo_email_template_prefill'); } catch (_) {}
    if (!raw) return;
    try { sessionStorage.removeItem('milo_email_template_prefill'); } catch (_) {}
    let payload = null;
    try { payload = JSON.parse(raw); } catch (_) { return; }
    const valid = (
      payload &&
      payload.action_id === 'messaging.open_compose_with_template_prefilled' &&
      payload.target &&
      payload.target.kind === 'messaging_email_template_prefill' &&
      payload.target.prefill &&
      typeof payload.target.prefill === 'object' &&
      typeof payload.target.prefill.template_id === 'string' &&
      payload.target.prefill.template_id.length > 0
    );
    if (!valid) {
      setMiloPrefillBanner({
        ok: false,
        title: 'Milo prefill ignored',
        detail: 'The prefill payload was malformed or did not match the expected shape. No fields were changed.',
      });
      return;
    }
    const pf = payload.target.prefill;
    // Force the Email subtab. The parent already set initialTab='email'
    // via onNavigate, but this guards the case where the parent's state
    // refresh hasn't propagated yet.
    setActiveTab('email');
    setSelectedTemplateId(pf.template_id);
    if (pf.search && typeof pf.search === 'string') {
      setTemplateSearch(pf.search);
    }
    setMiloPrefillBanner({
      ok: true,
      title: 'Milo prepared this view',
      summary: payload.summary,
      template_name: pf.template_name,
      template_subject: pf.template_subject,
      template_type: pf.template_type,
      detail:
        'Milo only preselected the template. Recipient checkboxes, the '
        + 'subject, the message body, the schedule date, and the existing '
        + 'Send button remain entirely yours. Milo did not send, save, '
        + 'submit, or modify any data.',
      runbook_family_id: payload.runbook_family_id,
      runbook_version: payload.runbook_version,
    });
  }, [initialTab]);
  // -----------------------------------------------------------------------

  // ═══ DATA FETCHING ════════════════════════════════════════════════════════════
  
  useEffect(() => {
    fetchTemplates();
    fetchEmailLogs();
  }, []);

  useEffect(() => {
    // Sync selectedGuests with external selectedIds
    if (selectedIds && selectedIds.length > 0) {
      setSelectedGuests(selectedIds);
    }
  }, [selectedIds]);

  const fetchTemplates = async () => {
    try {
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/api/email-templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/sms-templates`).catch(() => ({ data: [] })),
      ]);
      setEmailTemplates(emailRes.data || []);
      setSmsTemplates(smsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const res = await axios.get(`${API}/api/email-logs`);
      setEmailLogs(res.data || []);
    } catch (err) {
      console.error('Failed to fetch email logs:', err);
    }
  };

  // ═══ COMPUTED VALUES ══════════════════════════════════════════════════════════
  
  const currentTemplates = activeTab === 'sms' ? smsTemplates : emailTemplates;
  
  const filteredTemplates = useMemo(() => {
    let result = currentTemplates;
    
    // Filter by status
    if (templateFilter !== 'all') {
      result = result.filter(t => {
        const status = t.status === 'draft' ? 'draft' : 'ready';
        return status === templateFilter;
      });
    }
    
    // Filter by search
    if (templateSearch.trim()) {
      const search = templateSearch.toLowerCase();
      result = result.filter(t => 
        t.name?.toLowerCase().includes(search) ||
        t.subject?.toLowerCase().includes(search)
      );
    }
    
    return result;
  }, [currentTemplates, templateFilter, templateSearch]);

  const selectedTemplate = useMemo(() => {
    return currentTemplates.find(t => t.id === selectedTemplateId) || null;
  }, [currentTemplates, selectedTemplateId]);

  // Pre-flight checklist values
  const hasTemplateSelected = !!selectedTemplate;
  const hasContentVerified = !!selectedTemplate?.subject?.trim();
  const hasRecipientsConfirmed = selectedGuests.length > 0;
  const canPreview = hasTemplateSelected && hasRecipientsConfirmed;

  // ═══ HANDLERS ═════════════════════════════════════════════════════════════════
  
  const handleSelectTemplate = (templateId) => {
    setSelectedTemplateId(templateId === selectedTemplateId ? null : templateId);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
  };

  const handleOpenAIStudio = () => {
    setShowAIModal(true);
  };

  const handleOpenImport = () => {
    setShowImportModal(true);
  };

  // Part 2: Recipient handlers
  const handleToggleGuest = (guestId) => {
    setSelectedGuests(prev => 
      prev.includes(guestId) 
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const handleSelectAllGuests = () => {
    setSelectedGuests(guests.map(g => g.id));
  };

  const handleClearAllGuests = () => {
    setSelectedGuests([]);
  };

  const handleSend = async (templateId, recipientIds, scheduleDateTime) => {
    if (!templateId || recipientIds.length === 0) return;
    
    setIsSending(true);
    try {
      const response = await axios.post(`${API}/api/emails/send/${templateId}`, {
        guest_ids: recipientIds,
        schedule_at: scheduleDateTime || null,
      });
      
      // Refresh logs after send
      await fetchEmailLogs();
      
      // Clear selection after successful send
      setSelectedGuests([]);
      
      console.log('Send successful:', response.data);
    } catch (err) {
      console.error('Send failed:', err);
    } finally {
      setIsSending(false);
    }
  };

  // Compute delivery/open rates from logs
  const deliveryStats = useMemo(() => {
    if (emailLogs.length === 0) return { deliveryRate: '—', openRate: '—' };
    
    const total = emailLogs.length;
    const delivered = emailLogs.filter(l => l.status !== 'Failed').length;
    const opened = emailLogs.filter(l => l.status === 'Opened' || l.status === 'Clicked').length;
    
    return {
      deliveryRate: `${Math.round((delivered / total) * 100)}%`,
      openRate: `${Math.round((opened / total) * 100)}%`,
    };
  }, [emailLogs]);

  // ═══ RENDER ═══════════════════════════════════════════════════════════════════

  return (
    <div style={{ minHeight: '100%', background: COLORS.surface }} data-testid="messaging-tab">

      {/* Shared AdminPageHeader (Dashboard standard). The command bar below
          retains the live template/sent counts and its action controls; the
          duplicate "Communication Studio" heading was removed from the bar
          so the page title lives here only. */}
      <div style={{ padding: '20px 24px 0' }}>
        <AdminPageHeader
          eyebrow="Messaging"
          title="Communication Studio"
          tagline="Compose, preview, and send guest email and SMS from one place."
        />
      </div>

      {/* KPI integrity warning — only renders when integrity fails.
          Tells the admin that numbers referenced on this page may be unreliable. */}
      <KPIIntegrityIndicator integrityCheck={integrityCheck} variant="inline-warning" context="messaging" />

      {/* ═══ Item 4 — Milo template-assist banner ═══════════════════════════
          Renders only after Milo's L1 template-assist prefill has been
          consumed. Clearly tells the operator: Milo prepared the form,
          Milo did NOT send, save, submit, or execute anything. Operator
          can dismiss it at any time without affecting the prefilled
          template selection. */}
      {miloPrefillBanner && (
        <div
          data-testid="milo-email-prefill-banner"
          style={{
            margin: '12px 16px 0',
            padding: '12px 14px',
            border: `1px solid ${miloPrefillBanner.ok ? 'rgba(198,167,90,0.45)' : 'rgba(180,60,60,0.4)'}`,
            background: miloPrefillBanner.ok ? 'rgba(198,167,90,0.08)' : 'rgba(180,60,60,0.06)',
            borderRadius: 10,
            color: COLORS.text || '#1B1812',
            fontSize: 13,
            lineHeight: 1.55,
            display: 'flex', gap: 12, alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {miloPrefillBanner.title}
            </div>
            {miloPrefillBanner.summary && (
              <div style={{ marginBottom: 4 }}>
                <span style={{ opacity: 0.7 }}>Proposed action: </span>
                <span data-testid="milo-email-prefill-summary">{miloPrefillBanner.summary}</span>
              </div>
            )}
            {miloPrefillBanner.template_name && (
              <div style={{ marginBottom: 4 }}>
                <span style={{ opacity: 0.7 }}>Template preselected: </span>
                <strong data-testid="milo-email-prefill-template-name">
                  {miloPrefillBanner.template_name}
                </strong>
                {miloPrefillBanner.template_type && (
                  <span style={{ opacity: 0.7 }}> · type: <code>{miloPrefillBanner.template_type}</code></span>
                )}
              </div>
            )}
            <div style={{ marginTop: 4 }}>{miloPrefillBanner.detail}</div>
            {(miloPrefillBanner.runbook_family_id || miloPrefillBanner.runbook_version) && (
              <div style={{ marginTop: 6, fontSize: 11, opacity: 0.75 }}>
                Grounded in runbook: {miloPrefillBanner.runbook_family_id} v{miloPrefillBanner.runbook_version}
              </div>
            )}
          </div>
          <button
            data-testid="milo-email-prefill-banner-dismiss"
            onClick={() => setMiloPrefillBanner(null)}
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: '1px solid rgba(0,0,0,0.18)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              color: 'inherit',
            }}>
            Dismiss banner
          </button>
        </div>
      )}

      {/* ═══ B1-B5: COMMAND BAR ════════════════════════════════════════════════ */}
      <div style={styles.commandBar} data-testid="command-bar">
        <div style={styles.commandBarLeft}>
          {/* B1: Communication Studio Header */}
          <div style={styles.commandBarTitle}>
            <div style={styles.commandBarIcon}>
              <Mail size={20} color={COLORS.gold} />
            </div>
            <div>
              <div style={styles.commandBarSubheading}>
                {emailTemplates.length + smsTemplates.length} templates · {emailLogs.length} sent
              </div>
            </div>
          </div>
        </div>
        
        <div style={styles.commandBarRight}>
          {/* B4: Template Library Filter */}
          <div style={styles.filterChipRow}>
            {TEMPLATE_FILTERS.map(filter => (
              <button
                key={filter.key}
                style={{
                  ...styles.filterChip,
                  ...(templateFilter === filter.key ? styles.filterChipActive : {}),
                }}
                onClick={() => setTemplateFilter(filter.key)}
                data-testid={`filter-${filter.key}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          
          {/* B5: Search Input */}
          <div style={styles.searchInputWrapper}>
            <Search size={14} color={COLORS.textMuted} style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search templates..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              style={styles.searchInput}
              data-testid="template-search"
            />
          </div>
          
          {/* B3: Import Template Button */}
          <button
            style={styles.commandBtn}
            onClick={handleOpenImport}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = COLORS.surface;
              e.currentTarget.style.borderColor = COLORS.borderMedium;
            }}
            data-testid="import-template-btn"
          >
            <Upload size={16} />
            Import
          </button>
          
          {/* B2: AI Studio Button */}
          <button
            style={{ ...styles.commandBtn, ...styles.commandBtnPrimary }}
            onClick={handleOpenAIStudio}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(198,167,90,0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(198,167,90,0.25)';
            }}
            data-testid="ai-studio-btn"
          >
            <Sparkles size={16} />
            AI Studio
          </button>
        </div>
      </div>

      {/* ═══ A1-A6: TAB RAIL ═══════════════════════════════════════════════════ */}
      <div style={styles.tabRailShell} data-testid="tab-rail">
        <div style={styles.tabRow}>
          {TAB_DEFS.map(tab => {
            const active = activeTab === tab.key;
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.key}
                style={{
                  ...styles.tabChip,
                  ...(active ? styles.tabChipActive : {}),
                }}
                onClick={() => setActiveTab(tab.key)}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = COLORS.goldLight;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = COLORS.chipBg;
                  }
                }}
                data-testid={`tab-${tab.key}`}
              >
                <TabIcon size={16} color={active ? '#FFFFFF' : COLORS.textSecondary} />
                <span style={{
                  ...styles.tabChipText,
                  ...(active ? styles.tabChipTextActive : {}),
                }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ TAB CONTENT ══════════════════════════════════════════════════════ */}
      
      {activeTab === 'email' && (
        <div style={styles.emailStudio3Col} data-testid="email-studio">
          
          {/* LEFT COLUMN: Pre-Flight + Template Carousel */}
          <div style={styles.studioLeftColumn}>
            {/* C1-C9: Pre-Flight HUD */}
            <PreFlightHUD
              hasTemplateSelected={hasTemplateSelected}
              hasContentVerified={hasContentVerified}
              hasRecipientsConfirmed={hasRecipientsConfirmed}
              canPreview={canPreview}
            />
            
            {/* D1-D14: Template Carousel */}
            <TemplateCarousel
              templates={filteredTemplates}
              selectedTemplateId={selectedTemplateId}
              onSelectTemplate={handleSelectTemplate}
              onPreviewTemplate={handlePreviewTemplate}
              filterStatus={templateFilter}
            />
          </div>
          
          {/* CENTER COLUMN: E1-E10 Send Composer */}
          <div style={styles.studioCenterColumn}>
            <SendComposer
              selectedTemplate={selectedTemplate}
              guests={guests}
              selectedGuests={selectedGuests}
              onToggleGuest={handleToggleGuest}
              onSelectAll={handleSelectAllGuests}
              onClearAll={handleClearAllGuests}
              onPreviewTemplate={handlePreviewTemplate}
              onSend={handleSend}
              isSending={isSending}
              deliveryRate={deliveryStats.deliveryRate}
              openRate={deliveryStats.openRate}
            />
          </div>
          
          {/* RIGHT COLUMN: F1-F8 Activity Feed */}
          <div style={styles.studioRightColumn}>
            <ActivityFeed
              emailLogs={emailLogs}
              maxItems={8}
            />
          </div>
        </div>
      )}

      {activeTab === 'sms' && (
        <SMSStudio
          templates={smsTemplates}
          guests={guests}
          selectedGuests={selectedGuests}
          onToggleGuest={handleToggleGuest}
          onSelectAll={handleSelectAllGuests}
          onClearAll={handleClearAllGuests}
          onSend={(templateId, recipients) => {
            console.log('SMS send:', templateId, recipients);
            // SMS send would go to /api/sms/send endpoint
          }}
          isSending={isSending}
          smsStats={{ sent: 0, delivered: 0, failed: 0 }}
          isProviderConnected={false} // Set to true when SMS provider is connected
        />
      )}

      {activeTab === 'activity' && (
        <ActivityLogTab
          emailLogs={emailLogs}
          smsLogs={[]} // SMS logs would come from a separate endpoint
          guests={guests}
        />
      )}

      {activeTab === 'journey' && (
        <JourneyBuilder
          emailTemplates={emailTemplates}
          smsTemplates={smsTemplates}
          onBack={() => setActiveTab('email')}
        />
      )}

      {activeTab === 'compose' && (
        <div style={{ padding: '0 24px', background: COLORS.surface, minHeight: 'calc(100vh - 200px)' }}>
          <ComposeTab guests={guests} />
        </div>
      )}

      {activeTab === 'templates' && (
        <div style={{
          padding: '24px',
          background: COLORS.surface,
          minHeight: 'calc(100vh - 200px)',
        }} data-testid="templates-tab-content">
          <MessageTemplatesTab />
        </div>
      )}

      {/* ═══ K1-K7: TEMPLATE PREVIEW MODAL ═════════════════════════════════════ */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}

      {/* ═══ L1-L11: TEMPLATE IMPORT MODAL ═════════════════════════════════════ */}
      {showImportModal && (
        <TemplateImportModal
          onClose={() => setShowImportModal(false)}
          onSave={async (templateData) => {
            try {
              await axios.post(`${API}/api/email-templates`, templateData);
              await fetchTemplates();
              toast.success('Template imported');
            } catch (err) {
              console.error('Failed to import template:', err);
              toast.error('Failed to import template');
            }
          }}
        />
      )}

      {/* ═══ M1-M16: AI GENERATOR MODAL ════════════════════════════════════════ */}
      {showAIModal && (
        <AIGeneratorModal
          onClose={() => setShowAIModal(false)}
          onSaveAsDraft={async (templateData) => {
            try {
              await axios.post(`${API}/api/email-templates`, templateData);
              await fetchTemplates();
              toast.success('AI template saved as draft');
            } catch (err) {
              console.error('Failed to save AI template:', err);
              toast.error('Failed to save template');
            }
          }}
          onSendNow={(content) => {
            // Would trigger send flow with generated content
            toast.info('Send Now - UI Preview Only', {
              description: 'Full send integration pending.',
              duration: 4000,
            });
          }}
        />
      )}
    </div>
  );
};

export default MessagingTab;
